import { formatMobile } from "../../../common/MobileField";
import SoniaKnowledge from "./SoniaKnowledge";
import EditMemberDetails from "./EditMemberDetails";
import MemberStaffModal, { StaffModalMode } from "./MemberStaffModal";
import { displayAbn, displayAcn } from "../../../../Utils/companyNumber";
import { useState, ChangeEvent } from "react";
import { formatDateTime } from "../../../../Utils/formatDateTime";
import { useParams } from "react-router-dom";
import { useMemo } from "react";
import DataTable from "react-data-table-component";
import { Button, Spinner, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { toast } from "react-toastify";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import {
    getOperatorCompanyByIdService,
    updateOperatorStaffStatusService,
    getOperatorEoisService,
    getOperatorUsageService,
    getOperatorSearchesService,
    getPlatformStatesService,
    getOperatorCompanyServicesService,
    saveOperatorCompanyServiceService,
    getOperatorCompanyLeadsService,
    importOperatorCompanyLeadsService,
    loginAsOperatorCompanyService,
} from "../../../../Services/CRMAdminServices/CRMOperator";

// NOTE: adjust the relative import paths to match this file's real location.

const customStyles = {
    head: {
        style: { fontWeight: "800", color: "#0000ff", fontSize: "14px" },
    },
};

const dash = (val: any) => (val === null || val === undefined || val === "" ? "-" : val);

// Sonia's engine keeps every Gameplan login and plan. "Log in as client" goes there:
// it checks the SMS admin login, then opens Gameplan as that client, under their
// broker's brand. The broker's id goes with it so a client is only ever opened from
// their own broker's page.
const SONIA_ENGINE = "https://fintrack-sms.azurewebsites.net";
const gameplanLoginAs = (email: string, companyGuid: string) =>
    `${SONIA_ENGINE}/admin/gameplan/login-as?email=${encodeURIComponent(email)}&brand=${encodeURIComponent(companyGuid || "")}`;
// N21: API datetimes are UTC without a marker - formatDateTime reads them as UTC
// and renders Australian Eastern time.
const formatDate = (val: string | null) => formatDateTime(val);


const STRATEGY_LABELS: Record<string, string> = {
    Cashflow: "Income now",
    Balanced: "Balanced",
    Growth: "Long-term growth",
};

const displayStrategy = (value: any) => {
    if (!value) return "-";
    return STRATEGY_LABELS[value] || value;
};

const formatSearchParams = (params: any, stateIdToName: Record<string, string>) => {
    if (!params) return "-";
    const budget = `Up to $${(params.budgetMax ?? 0).toLocaleString()}`;
    const type = params.propertyType ? params.propertyType : "All";
    const states = params.stateIds
        ? params.stateIds
              .split(",")
              .map((id: string) => stateIdToName[id.trim()] || id.trim())
              .join(", ")
        : "All";
    const bedrooms =
        params.bedroomsMin || params.bedroomsMax
            ? `${params.bedroomsMin ?? "any"}-${params.bedroomsMax ?? "any"} beds`
            : "All";
    const contractType = params.contractType ? params.contractType : "All";
    const propertyReady = params.propertyReady ? params.propertyReady : "All";

    return [
        `Budget: ${budget}`,
        `Type: ${type}`,
        `States: ${states}`,
        `Bedrooms: ${bedrooms}`,
        `Contract: ${contractType}`,
        `PropertyReady: ${propertyReady}`,
    ].join(" \u00b7 ");
};
const OperatorMemberDetail = () => {
    const { companyId } = useParams<{ companyId: string }>();

    // Which services this member is on, and what they pay. The endpoint is
    // behind the Operator guard, so a fee never reaches a member's own brokers.
    const {
        data: servicesData,
        refetch: refetchServices,
    } = useGetRequestQuery<any>(
        ["getOperatorCompanyServices", companyId],
        () => getOperatorCompanyServicesService(companyId!),
        { enabled: !!companyId }
    );
    const services: any[] = servicesData?.data || [];
    const [savingService, setSavingService] = useState<string | null>(null);

    // The member's own client list - what Sonia rings. Imported from a CSV the
    // client exports from whatever system they are on.
    const {
        data: leadsData,
        refetch: refetchLeads,
    } = useGetRequestQuery<any>(
        ["getOperatorCompanyLeads", companyId],
        () => getOperatorCompanyLeadsService(companyId!),
        { enabled: !!companyId }
    );
    const memberLeads: any[] = leadsData?.data || [];
    const [isImporting, setIsImporting] = useState(false);
    const [importSummary, setImportSummary] = useState<any>(null);

    const downloadLeadTemplate = () => {
        // Built here rather than served as a file so the headings can never
        // drift from what the importer actually looks for.
        const csv = [
            "First Name,Surname,Mobile,Email,Source,Notes",
            "Jane,Citizen,0412 345 678,jane@example.com.au,Website,Refinance enquiry",
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Fintrack lead import template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleLeadFile = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        setIsImporting(true);
        setImportSummary(null);
        try {
            const response: any = await importOperatorCompanyLeadsService(companyId!, file);
            const summary = response?.data ?? response;
            setImportSummary(summary);
            toast.success(`${summary?.imported ?? 0} imported, ${summary?.skipped ?? 0} skipped, ${summary?.failed ?? 0} failed.`);
            await refetchLeads();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Could not import that file.");
        } finally {
            setIsImporting(false);
        }
    };

    const saveService = async (row: any, changes: any) => {
        setSavingService(row.serviceCode);
        try {
            await saveOperatorCompanyServiceService({
                companyId: companyId!,
                serviceCode: row.serviceCode,
                isActive: row.isActive,
                startDate: row.startDate ?? null,
                monthlyFee: row.monthlyFee ?? null,
                commissionIncluded: row.commissionIncluded ?? null,
                notes: row.notes ?? null,
                ...changes,
            });
            await refetchServices();
        } catch {
            toast.error("Could not save that service.");
        } finally {
            setSavingService(null);
        }
    };

    const [editDetailsOpen, setEditDetailsOpen] = useState(false);

    // Add, edit or delete one of this member's staff.
    const [staffEdit, setStaffEdit] = useState<{ mode: StaffModalMode; staff: any | null } | null>(null);

    // Their clients' Gameplan link, the one the member also sees in their own Platform.
    const gameplanLink = (guid: string) => `https://fintrack.com.au/gameplan?b=${guid}`;
    const copyGameplanLink = async (guid: string) => {
        try {
            await navigator.clipboard.writeText(gameplanLink(guid));
            toast.success("Gameplan link copied.");
        } catch {
            toast.error("Could not copy. Select the link and copy it by hand.");
        }
    };

    // "I virtually log in as the MB." Opens the Platform as this member in a new tab.
    // The tab is opened before the link is fetched: a tab opened after waiting for a
    // reply is treated by the browser as a pop-up and blocked.
    const [isLoggingInAs, setIsLoggingInAs] = useState(false);
    const loginAsMember = async () => {
        const tab = window.open("", "_blank");
        setIsLoggingInAs(true);
        try {
            const res: any = await loginAsOperatorCompanyService(companyId!);
            const url = res?.data?.url || res?.data?.Url;
            if (!url) throw new Error("no link");
            if (tab) tab.location.href = url;
            else window.location.href = url;
        } catch (err: any) {
            if (tab) tab.close();
            toast.error(err?.response?.data?.message || "Could not open this member's Platform.");
        } finally {
            setIsLoggingInAs(false);
        }
    };

    const [staffModal, setStaffModal] = useState(false);
    const [pendingStaff, setPendingStaff] = useState<any>(null);
    const [staffConflictMsg, setStaffConflictMsg] = useState<string | null>(null);

    const [usageFrom, setUsageFrom] = useState("");
    const [usageTo, setUsageTo] = useState("");

    const [searchPage, setSearchPage] = useState(1);
    const searchPageSize = 20;

    const {
        data: companyData,
        isLoading,
        isError,
        refetch,
    } = useGetRequestQuery<any>(["getOperatorCompanyById", companyId], () => getOperatorCompanyByIdService(companyId!), {
        enabled: !!companyId,
    });

    const company = !isError ? companyData?.data : null;
  

const { data: statesData } = useGetRequestQuery<any>(
    ["getPlatformStates"],
    () => getPlatformStatesService(),
    {}
);

// stateId -> stateName lookup, built once the states list loads
const stateIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    (statesData?.data || []).forEach((s: any) => {
        map[String(s.stateId)] = s.stateName;
    });
    return map;
}, [statesData]);
    const { data: eoiData, isLoading: eoiLoading } = useGetRequestQuery<any>(
        ["getOperatorCompanyEois", companyId],
        () => getOperatorEoisService({ companyId }),
        { enabled: !!companyId }
    );
    // Newest EOI first, always - sorted here explicitly instead of trusting
    // the API's order, since that wasn't reliably newest-first.
    const eois = [...(eoiData?.data || [])].sort(
        (a: any, b: any) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
    );

    const {
        data: usageData,
        isLoading: usageLoading,
        refetch: refetchUsage,
    } = useGetRequestQuery<any>(
        ["getOperatorUsage", companyId, usageFrom, usageTo],
        () =>
            getOperatorUsageService(companyId!, {
                ...(usageFrom ? { fromDate: usageFrom } : {}),
                ...(usageTo ? { toDate: usageTo } : {}),
            }),
        { enabled: !!companyId }
    );
    const usage = usageData?.data;

    const { data: searchesData, isLoading: searchesLoading } = useGetRequestQuery<any>(
        ["getOperatorCompanySearches", companyId, searchPage],
        () =>
            getOperatorSearchesService({
                companyId,
                pageNumber: searchPage,
                pageSize: searchPageSize,
            }),
        { enabled: !!companyId }
    );
    // searches[] is already newest-first from the API - never re-sort it here.
    const searches = searchesData?.data?.searches || [];
    const totalSearches = searchesData?.data?.totalSearches ?? 0;

    const { mutate: toggleStaffStatus, isPending: isTogglingStaff } = useMutationRequestQuery(
        "updateOperatorStaffStatus",
        updateOperatorStaffStatusService,
        {
            onSuccess: (res: any) => {
                toast.success(res?.data?.message || "Login status updated.");
                setStaffModal(false);
                setPendingStaff(null);
                setStaffConflictMsg(null);
                refetch();
            },
            onError: (err: any) => {
                const status = err?.response?.status;
                if (status === 409) {
                    // Normal - the API refuses to leave an active company with nobody who can log in
                    setStaffConflictMsg(
                        err?.response?.data?.message ||
                            "This is the only active login for the company. Disable the whole member account instead."
                    );
                } else if (status === 404) {
                    toast.error("Staff member not found. Refreshing the list.");
                    setStaffModal(false);
                    setPendingStaff(null);
                    refetch();
                } else {
                    toast.error(err?.response?.data?.message || "Failed to update login status.");
                    setStaffModal(false);
                    setPendingStaff(null);
                }
            },
        }
    );

    const openStaffModal = (staff: any) => {
        setPendingStaff(staff);
        setStaffConflictMsg(null);
        setStaffModal(true);
    };

    const confirmStaffToggle = () => {
        if (!pendingStaff) return;
        toggleStaffStatus({ staffId: pendingStaff.staffId, isActive: !pendingStaff.isActive });
    };

    const staffColumns = [
        {
            name: "Name",
            cell: (row: any) => (
                <span>
                    {row.firstName} {row.surname}
                    {row.isOwner && (
                        <Badge color="info" className="ms-2">
                            Owner
                        </Badge>
                    )}
                </span>
            ),
            width: "12rem",
        },
        { name: "Mobile", selector: (row: any) => dash(formatMobile(row.mobile)), width: "9rem" },
        { name: "Email", selector: (row: any) => dash(row.email), width: "14rem" },
        {
            name: "Verified",
            cell: (row: any) => (row.isEmailVerified ? "✓" : "✗"),
            width: "7rem",
        },
        {
            name: "Password set",
            cell: (row: any) => (row.hasSetPassword ? "✓" : "Invited, not activated"),
            width: "10rem",
        },
        {
            name: "Last login",
            cell: (row: any) => formatDate(row.lastLoginOn),
            width: "11rem",
        },
        {
            name: "Status",
            width: "12rem",
            cell: (row: any) =>
                company.isActive ? (
                    <div className="d-flex align-items-center gap-2">
                        <Badge color={row.isActive ? "success" : "secondary"}>
                            {row.isActive ? "Active" : "Disabled"}
                        </Badge>
                        <Button
                            size="sm"
                            outline
                            color={row.isActive ? "danger" : "success"}
                            onClick={() => openStaffModal(row)}
                        >
                            {row.isActive ? "Disable" : "Enable"}
                        </Button>
                    </div>
                ) : (
                    <Badge color="secondary">Disabled</Badge>
                ),
        },
        {
            name: "",
            width: "10rem",
            cell: (row: any) => (
                <div className="d-flex align-items-center gap-2">
                    <Button size="sm" outline color="primary" onClick={() => setStaffEdit({ mode: "edit", staff: row })}>
                        Edit
                    </Button>
                    {!row.isOwner && (
                        <Button size="sm" outline color="danger" onClick={() => setStaffEdit({ mode: "delete", staff: row })}>
                            Delete
                        </Button>
                    )}
                </div>
            ),
        },
    ];

  const searchColumns = [
    { name: "Date", selector: (row: any) => formatDate(row.createdOn), width: "11rem" },
    {
        name: "Staff Member",
        cell: (row: any) => <span title={row.staffEmail}>{dash(row.staffMember)}</span>,
        width: "10rem",
    },
    { name: "Strategy", selector: (row: any) => displayStrategy(row.strategy), width: "8rem" },
    {
        name: "Filters",
        cell: (row: any) => <span>{formatSearchParams(row.parameters, stateIdToName)}</span>,
        grow: 2,
    },
    { name: "Results", selector: (row: any) => row.resultCount, width: "7rem" },
];
    const searchTotalPages = Math.max(1, Math.ceil(totalSearches / searchPageSize));

   const eoiColumns = [
    { name: "Date", selector: (row: any) => formatDate(row.createdOn), width: "12rem" },
    { name: "Staff Member", selector: (row: any) => dash(row.staffMember), width: "12rem" },
    {
        name: "Property Address",
        cell: (row: any) => (
            <span title={`Property ID: ${row.propertyId}`}>{dash(row.propertyAddress)}</span>
        ),
        width: "18rem",
    },
    {
        name: "Note",
        cell: (row: any) => <span title={row.noteToFintrack}>{dash(row.noteToFintrack)}</span>,
        grow: 1,
    },
];

    if (isLoading) {
        return (
            <div className="mt-4 mb-2" style={{ textAlign: "center" }}>
                <Spinner color="primary" />
            </div>
        );
    }

    if (isError || !company) {
        return (
            <div className="mt-4 mb-2" style={{ textAlign: "center" }}>
                <h6>Member not found.</h6>
            </div>
        );
    }

    return (
        <>
            <div className="px-4 pt-4">
                <div
                    className="p-3 mb-3"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
                >
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <h3 className="f-w-900 heading-color mb-0">{company.businessName}</h3>
                        <div className="d-flex align-items-center" style={{ gap: 10 }}>
                            <Button color="primary" size="sm" onClick={loginAsMember} disabled={isLoggingInAs}>
                                {isLoggingInAs ? "Opening..." : "Log in as this broker"}
                            </Button>
                            <Badge color={company.isActive ? "success" : "secondary"} style={{ fontSize: "14px" }}>
                                {company.isActive ? "Active" : "Disabled"}
                            </Badge>
                        </div>
                    </div>

                    <div className="row">
                        {/* Profile panel - Fintrack edits every detail from here ("Edit details") */}
                        <div className="col-md-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="f-w-700 mb-0">Profile</h6>
                                <Button color="primary" size="sm" onClick={() => setEditDetailsOpen(true)}>
                                    Edit details
                                </Button>
                            </div>
                           {company.logoUrl && (
        <div
            style={{
                width: "100%",
                maxWidth: 220,
                height: 140,
                border: "1px solid #eee",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 12,
                marginBottom: 16,
                backgroundColor: "#fafafa",
            }}
        >
            <img
                src={company.logoUrl}
                alt={`${company.businessName} logo`}
                style={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                }}
            />
        </div>
    )}
                            {company.contactPhotoUrl && (
                                <p>
                                    <strong>Contact photo:</strong>
                                    <br />
                                    <img
                                        src={company.contactPhotoUrl}
                                        alt={`${company.contactName || company.businessName} photo`}
                                        style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", marginTop: 6 }}
                                    />
                                </p>
                            )}
                            <p>
                                <strong>Short name:</strong> {dash(company.shortName)}
                            </p>
                            {company.companyGuid && (
                                <p>
                                    <strong>Their clients' Gameplan link:</strong>
                                    <br />
                                    <a href={gameplanLink(company.companyGuid)} target="_blank" rel="noreferrer" style={{ wordBreak: "break-all" }}>
                                        {gameplanLink(company.companyGuid)}
                                    </a>
                                    <br />
                                    <Button size="sm" outline color="primary" className="mt-1" onClick={() => copyGameplanLink(company.companyGuid)}>
                                        Copy link
                                    </Button>
                                </p>
                            )}
                            <p>
                                <strong>ACN:</strong> {dash(displayAcn(company.acn))}
                            </p>
                            <p>
                                <strong>ABN:</strong> {dash(displayAbn(company.abn))}
                            </p>
                            <p>
                                <strong>Contact:</strong> {dash(company.contactName)}
                            </p>
                            <p>
                                <strong>Mobile:</strong> {dash(formatMobile(company.contactMobile))}
                            </p>
                            <p>
                                <strong>Email:</strong> {dash(company.contactEmail)}
                            </p>
                            {company.bookingLink && (
                                <p>
                                    <strong>Booking link:</strong>{" "}
                                    <a href={company.bookingLink} target="_blank" rel="noreferrer">
                                        {company.bookingLink}
                                    </a>
                                </p>
                            )}
                            <p>
                                <strong>Brand colours:</strong>
                                <br />
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 16,
                                        height: 16,
                                        backgroundColor: company.brandColorPrimary || "#fff",
                                        border: "1px solid #ccc",
                                        marginRight: 6,
                                        verticalAlign: "middle",
                                    }}
                                />
                                {dash(company.brandColorPrimary)}{" "}
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 16,
                                        height: 16,
                                        backgroundColor: company.brandColorSecondary || "#fff",
                                        border: "1px solid #ccc",
                                        marginLeft: 12,
                                        marginRight: 6,
                                        verticalAlign: "middle",
                                    }}
                                />
                                {dash(company.brandColorSecondary)}
                            </p>
                            <p>
                                <strong>Created:</strong> {formatDate(company.createdOn)}
                            </p>
                            <p>
                                <strong>Updated:</strong> {formatDate(company.updatedOn)}
                            </p>
                            <p>
                                <strong>Owner:</strong> {dash(company.ownerName)} ({dash(company.ownerEmail)})
                            </p>
                        </div>

                        {/* Stat tiles + usage date range */}
                        <div className="col-md-8">
                            <h6 className="f-w-700 mb-3">Usage</h6>
                            <div className="d-flex gap-4 flex-wrap mb-3">
                                <StatTile label="Staff" value={company.staffCount} />
                                <StatTile label="Searches" value={usage ? usage.searchCount : company.searchCount} />
                                <StatTile label="Reports" value={usage ? usage.reportCount : company.reportCount} />
                                <StatTile label="EOIs" value={usage ? usage.eoiCount : company.eoiCount} />
                                <StatTile label="Last Activity" value={formatDate(company.lastActivityOn)} />
                            </div>

                            <div className="d-flex align-items-end gap-2 mb-2">
                                <div>
                                    <label className="d-block">From</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={usageFrom}
                                        onChange={(e) => setUsageFrom(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="d-block">To</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={usageTo}
                                        onChange={(e) => setUsageTo(e.target.value)}
                                    />
                                </div>
                                <Button color="primary" onClick={() => refetchUsage()} disabled={usageLoading}>
                                    {usageLoading ? "Loading..." : "Apply"}
                                </Button>
                                {(usageFrom || usageTo) && (
                                    <Button
                                        color="secondary"
                                        outline
                                        onClick={() => {
                                            setUsageFrom("");
                                            setUsageTo("");
                                        }}
                                    >
                                        All-time
                                    </Button>
                                )}
                            </div>
                            <small className="text-muted">
                                Leave dates blank for all-time counts. The tiles above show the selected range once
                                applied.
                            </small>
                        </div>
                    </div>
                </div>

                {/* Services and fees */}
                <div
                    className="p-3 mb-3"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
                >
                    <h6 className="f-w-700 mb-3">Services and fees</h6>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: "22%" }}>Service</th>
                                    <th style={{ width: "10%" }}>On</th>
                                    <th style={{ width: "16%" }}>Start date</th>
                                    <th style={{ width: "16%" }}>Monthly fee</th>
                                    <th style={{ width: "16%" }}>Commission</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {services.map((row: any) => (
                                    <tr key={row.serviceCode}>
                                        <td className="f-w-600">{row.serviceName}</td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={!!row.isActive}
                                                disabled={savingService === row.serviceCode}
                                                onChange={(e) => saveService(row, { isActive: e.target.checked })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={row.startDate ? String(row.startDate).slice(0, 10) : ""}
                                                disabled={savingService === row.serviceCode}
                                                onChange={(e) => saveService(row, { startDate: e.target.value || null })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                placeholder="0.00"
                                                defaultValue={row.monthlyFee ?? ""}
                                                disabled={savingService === row.serviceCode}
                                                onBlur={(e) =>
                                                    saveService(row, {
                                                        monthlyFee: e.target.value === "" ? null : Number(e.target.value),
                                                    })
                                                }
                                            />
                                        </td>
                                        <td>
                                            {row.hasCommission ? (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={row.commissionIncluded === null || row.commissionIncluded === undefined ? "" : row.commissionIncluded ? "1" : "0"}
                                                    disabled={savingService === row.serviceCode}
                                                    onChange={(e) =>
                                                        saveService(row, {
                                                            commissionIncluded: e.target.value === "" ? null : e.target.value === "1",
                                                        })
                                                    }
                                                >
                                                    <option value="">-</option>
                                                    <option value="1">Commission Included</option>
                                                    <option value="0">No Commission</option>
                                                </select>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                defaultValue={row.notes ?? ""}
                                                maxLength={500}
                                                disabled={savingService === row.serviceCode}
                                                onBlur={(e) => saveService(row, { notes: e.target.value || null })}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <small className="text-muted d-block mt-2">
                        Changes save as soon as you leave a field. Fees are visible to Fintrack only.
                    </small>
                </div>

                {/* What Sonia knows about this business - Fintrack writes it, they read it. */}
                <SoniaKnowledge companyId={companyId!} />

                {/* The member's own client list */}
                <div
                    className="p-3 mb-3"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
                >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="f-w-700 mb-0">Their clients ({memberLeads.length})</h6>
                        <div className="d-flex gap-2">
                            <Button color="secondary" size="sm" onClick={downloadLeadTemplate}>
                                Download template
                            </Button>
                            <label className="btn btn-primary btn-sm mb-0">
                                {isImporting ? "Importing . . ." : "Import CSV"}
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    hidden
                                    disabled={isImporting}
                                    onChange={handleLeadFile}
                                />
                            </label>
                        </div>
                    </div>

                    {importSummary && (
                        <div className="alert alert-light border mb-3 py-2">
                            <strong>{importSummary.imported}</strong> imported,{" "}
                            <strong>{importSummary.skipped}</strong> skipped,{" "}
                            <strong>{importSummary.failed}</strong> failed, out of {importSummary.totalRows} rows.
                            {(importSummary.rows || [])
                                .filter((r: any) => r.outcome !== "IMPORTED")
                                .slice(0, 10)
                                .map((r: any) => (
                                    <div key={r.rowNumber} className="small text-muted">
                                        Row {r.rowNumber}{r.mobile ? ` (${r.mobile})` : ""}: {r.reason}
                                    </div>
                                ))}
                        </div>
                    )}

                    <DataTable
                        columns={[
                            { name: "Name", selector: (row: any) => dash(`${row.firstName || ""} ${row.surname || ""}`.trim()), sortable: true },
                            { name: "Mobile", selector: (row: any) => dash(formatMobile(row.mobile)), width: "9rem" },
                            { name: "Email", selector: (row: any) => dash(row.email) },
                            { name: "Source", selector: (row: any) => dash(row.source), width: "9rem" },
                            { name: "Broker", selector: (row: any) => dash(row.assignedStaffName), width: "10rem" },
                            { name: "Added", selector: (row: any) => formatDateTime(row.createdOn), width: "11rem" },
                            {
                                name: "Gameplan",
                                width: "10rem",
                                cell: (row: any) =>
                                    row.email ? (
                                        <a href={gameplanLoginAs(row.email, company.companyGuid)} target="_blank" rel="noreferrer">
                                            Log in as client
                                        </a>
                                    ) : (
                                        <span className="text-muted">No email</span>
                                    ),
                            },
                        ]}
                        data={memberLeads}
                        customStyles={customStyles}
                        pagination
                        responsive
                        noDataComponent={<div className="py-3 text-muted">No clients imported yet.</div>}
                    />
                    <small className="text-muted d-block mt-2">
                        Columns are matched by heading, so the order does not matter. Only the mobile is
                        required. A mobile already on file is skipped, so a corrected file can be sent again
                        without doubling anyone up.
                    </small>
                </div>

                {/* Staff table */}
                <div
                    className="p-3 mb-3"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
                >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="f-w-700 mb-0">Staff</h6>
                        <Button color="primary" size="sm" onClick={() => setStaffEdit({ mode: "add", staff: null })}>
                            Add staff
                        </Button>
                    </div>
                    <DataTable columns={staffColumns} data={company.staff || []} customStyles={customStyles} responsive />
                </div>

                {/* EOI table */}
                <div
                    className="p-3 mb-4"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
                >
                    <h6 className="f-w-700 mb-3">EOIs</h6>
                    {eoiLoading ? (
                        <Spinner color="primary" />
                    ) : eois.length === 0 ? (
                        <p className="text-muted mb-0">This company hasn't submitted any Expressions of Interest yet.</p>
                    ) : (
                        <DataTable columns={eoiColumns} data={eois} customStyles={customStyles} responsive />
                    )}
                </div>

                {/* Searches table - individual property searches this company has made.
                    No client names come back from this endpoint (by design), and the
                    API already returns newest-first - never re-sorted here. */}
                <div
                    className="p-3 mb-4"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(45, 44, 44, 0.1)", backgroundColor: "#fff" }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="f-w-700 mb-0">Searches</h6>
                        {!searchesLoading && <span className="text-muted">Total: {totalSearches}</span>}
                    </div>
                    {searchesLoading ? (
                        <Spinner color="primary" />
                    ) : searches.length === 0 ? (
                        <p className="text-muted mb-0">No property searches have been run by this company's staff yet.</p>
                    ) : (
                        <>
                            <DataTable columns={searchColumns} data={searches} customStyles={customStyles} responsive />
                            {searchTotalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        outline
                                        disabled={searchPage <= 1}
                                        onClick={() => setSearchPage((p) => Math.max(1, p - 1))}
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-muted">
                                        Page {searchPage} of {searchTotalPages}
                                    </span>
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        outline
                                        disabled={searchPage >= searchTotalPages}
                                        onClick={() => setSearchPage((p) => Math.min(searchTotalPages, p + 1))}
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Enable / Disable staff login confirmation */}
            <MemberStaffModal
                companyId={companyId!}
                mode={staffEdit?.mode || "add"}
                staff={staffEdit?.staff || null}
                isOpen={!!staffEdit}
                toggle={() => setStaffEdit(null)}
                onSaved={() => refetch()}
            />

            <EditMemberDetails
                company={company}
                isOpen={editDetailsOpen}
                toggle={() => setEditDetailsOpen(false)}
                onSaved={() => refetch()}
            />

            <Modal isOpen={staffModal} toggle={() => setStaffModal(!staffModal)} centered>
                <ModalHeader toggle={() => setStaffModal(!staffModal)}>
                    {pendingStaff?.isActive ? "Disable login" : "Enable login"}
                </ModalHeader>
                <ModalBody>
                    {staffConflictMsg ? (
                        <div className="alert alert-warning mb-0">{staffConflictMsg}</div>
                    ) : (
                        <>
                            {pendingStaff?.isActive ? "Disable" : "Enable"} the login for{" "}
                            <strong>
                                {pendingStaff?.firstName} {pendingStaff?.surname}
                            </strong>
                            ?
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setStaffModal(false)}>
                        Close
                    </Button>
                    {!staffConflictMsg && (
                        <Button
                            color={pendingStaff?.isActive ? "danger" : "success"}
                            onClick={confirmStaffToggle}
                            disabled={isTogglingStaff}
                        >
                            {isTogglingStaff ? "Saving..." : pendingStaff?.isActive ? "Disable" : "Enable"}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </>
    );
};

const StatTile = ({ label, value }: { label: string; value: any }) => (
    <div
        style={{
            border: "1px solid #eee",
            borderRadius: 8,
            padding: "10px 16px",
            minWidth: 100,
            textAlign: "center",
        }}
    >
        <div style={{ fontSize: 20, fontWeight: 800 }}>{value ?? "-"}</div>
        <div style={{ fontSize: 12, color: "#888" }}>{label}</div>
    </div>
);

export default OperatorMemberDetail;